/**
 * loaderPreProcessJson_Dynamic.ts - JSON Dynamic Data Support for LoaderPreProcessJson
 * Applies dynamic data handlers from Global/dynamicdata.json if configured
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { RegistryStore } from '../../../../Assembler/dist/registry/registryStore.js';
import { JsonUtility } from '../../../../Assembler/dist/app/jsonUtility.js';
import { JsonArray, JsonObject, JsonValue, JsonValueType } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { normalizeFileContent, CommonUtil } from '../../../../Assembler/dist/common/commonUtil.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import { DynamicConfigMap } from '../../../../Assembler/dist/registry/dynamicConfigMap.js';
import { AppSiteResult } from '../../../../Assembler/dist/registry/iface/appSiteResult.js';
import type { DynamicConfig } from '../../../../Assembler/dist/registry/dynamicConfig.js';
import { buildParentContext } from './loaderPreProcessJson_Inheritance.js';
import type { LoaderPreProcessJson } from './loaderPreProcessJson.js';
import { NavigationDecision } from '../../../../Assembler/dist/model/index.js';

type PlainJsonObject = { [key: string]: unknown };
type PlainJsonMap = globalThis.Map <string, unknown>;

// #region Helper Functions

function getObjectKeys(obj: unknown): string[] {
    if (!obj) return [];
    if (obj instanceof JsonObject) return Array.from(obj.keys());
    if (obj instanceof DynamicConfigMap || obj instanceof Map) {
        return Array.from((obj as PlainJsonMap).keys());
    }
    if (typeof (obj as { keys?: unknown }).keys === 'function') {
        return (obj as { keys: () => string[] }).keys();
    }
    return Object.keys(obj as PlainJsonObject);
}

function hasKey(obj: unknown, key: string): boolean {
    if (!obj) return false;
    if (obj instanceof JsonObject) return obj.containsKey(key);
    if (obj instanceof DynamicConfigMap || obj instanceof Map) {
        return (obj as PlainJsonMap).has(key);
    }
    if (typeof (obj as { containsKey?: unknown }).containsKey === 'function') {
        return (obj as { containsKey: (key: string) => boolean }).containsKey(key);
    }
    return key in (obj as PlainJsonObject);
}

function getObjectValue(obj: unknown, key: string): unknown {
    if (!obj) return undefined;
    if (obj instanceof JsonObject) return obj.get(key);
    if (obj instanceof DynamicConfigMap || obj instanceof Map) {
        return (obj as PlainJsonMap).get(key);
    }
    if (typeof (obj as { get?: unknown }).get === 'function') {
        return (obj as { get: (key: string) => unknown }).get(key);
    }
    return (obj as PlainJsonObject)[key];
}

function setObjectValue(obj: unknown, key: string, value: unknown): void {
    if (!obj) return;
    if (obj instanceof JsonObject) {
        obj.set(key, value);
    } else if (obj instanceof Map) {
        obj.set(key, value);
    } else {
        (obj as PlainJsonObject)[key] = value;
    }
}

function findKeyCaseInsensitive(obj: unknown, key: string): string | null {
    if (!obj || !key) return null;
    const lowerKey = key.toLowerCase();
    for (const k of getObjectKeys(obj)) {
        if (k.toLowerCase() === lowerKey) {
            return k;
        }
    }
    return null;
}

// #endregion

function createJsonObjectWrapper(obj: PlainJsonObject | PlainJsonMap, readOnly: boolean = false): JsonObject {
    if (obj instanceof JsonObject) {
        return obj;
    }

    const result = new JsonObject();
    if (obj instanceof Map) {
        for (const [key, value] of obj) {
            result.set(key, value);
        }
    } else {
        for (const [key, value] of Object.entries(obj)) {
            result.set(key, value);
        }
    }
    result.isReadOnly = readOnly;
    return result;
}

function convertJsonArrayToPlain(jsonArray: JsonArray): unknown[] {
    const plainArray: unknown[] = [];
    for (let i = 0; i < jsonArray.length; i++) {
        const item = jsonArray[i];
        if (item instanceof JsonObject) {
            plainArray.push(convertJsonObjectToPlain(item));
        } else if (item instanceof JsonArray) {
            plainArray.push(convertJsonArrayToPlain(item));
        } else {
            plainArray.push(item);
        }
    }
    return plainArray;
}

function convertJsonObjectToPlain(jsonObject: JsonObject): PlainJsonObject {
    const plainObject: PlainJsonObject = {};
    for (const key of jsonObject.keys()) {
        const value = jsonObject.get(key);
        if (value instanceof JsonObject) {
            plainObject[key] = convertJsonObjectToPlain(value);
        } else if (value instanceof JsonArray) {
            plainObject[key] = convertJsonArrayToPlain(value);
        } else {
            plainObject[key] = value;
        }
    }
    return plainObject;
}

// DynamicView Support


/**
 * Dynamic instance methods to be attached to LoaderPreProcessJson prototype
 */
export const dynamicMethods = {
    hasDynamicDataHandlers(this: LoaderPreProcessJson, appSite: string, componentName: string): boolean {
        const config = this.getDynamicDataConfig(appSite);
        if (!config) return false;
        if (config.has('*')) return true;
        const name = componentName.toLowerCase();
        for (const key of config.keys()) if (key.toLowerCase() === name) return true;
        return false;
    },

    hasDynamicActionHandlers(this: LoaderPreProcessJson, appSite: string, componentName: string): boolean {
        const config = this.getDynamicActionConfig(appSite);
        if (!config) return false;
        if (config.has('*')) return true;
        const name = componentName.toLowerCase();
        for (const key of config.keys()) if (key.toLowerCase() === name) return true;
        return false;
    },

    getTemplateWithView(this: LoaderPreProcessJson, appSite: string, currentTemplateName: string, componentName: string, appView: string | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, componentDepthOverride: number | null = null, clientMode: boolean = false) {
        // clientMode threads into getTemplate → the inheritance seam, where applyClientHandlers consumes the
        // delivered/rendered client-data island. GetTemplateWithView itself only applies view handlers (WS-A).
        const result = this.getTemplate(appSite, currentTemplateName, componentName, appView, flowStack, configJson, clientMode);
        const htmlJson = this.applyDynamicViewHandlersJson(appSite, appView || '', currentTemplateName, componentName, componentDepthOverride ?? (flowStack?.length ?? 0), result.htmlJson, result.dataJson, result.resolvedAppSite, result.resolvedTemplateName, configJson);
        return { ...result, htmlJson };
    },

    tryBuildParentJsonValueHandlerForAction(this: LoaderPreProcessJson, appSite: string, flowStack: string[] | null, contextJson: JsonObject | null = null): ((key: string) => string | null) | null {
        const parentContext = flowStack && flowStack.length > 0
            ? buildParentContext(flowStack, this._dataJsonCache, appSite, this._dynamicResultsJson.resolvedTemplateJson)
            : null;
        if (!parentContext && !contextJson) {
            return null;
        }
        return (key: string): string | null => {
            const parentValue = parentContext?.get(key.toLowerCase());
            if (parentValue !== undefined && parentValue !== '') {
                return parentValue;
            }
            if (contextJson?.containsKey(key)) {
                // F-SEC1: surface non-string scalars (notably the IsAuthenticated bool) as their string form —
                // asString() returns null for a bool/number, which previously hid IsAuthenticated from the
                // in-handler authz check and forced role-only authz.
                const ctxRaw = contextJson.get(key);
                let ctxValue = ctxRaw.asString();
                if ((ctxValue === null || ctxValue === '') && !ctxRaw.isNull) {
                    ctxValue = ctxRaw.toString();
                }
                if (ctxValue !== null && ctxValue !== '') {
                    return ctxValue;
                }
            }
            return parentValue ?? null;
        };
    },

    applyDynamicDataHandlers(
        this: LoaderPreProcessJson,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentJson: JsonObject | PlainJsonObject,
        configJson: JsonObject | null = null,
        flowStack: string[] | null = null
    ): void {
        const { config: dynamicDataConfig, configAppSite: dynamicConfigAppSite } = this.resolveDynamicDataConfigSource(appSite);
        if (!dynamicDataConfig) {
            return;
        }

        const matchedKey = this.getDynamicDataMatchKey(dynamicDataConfig, componentName);
        if (!matchedKey) {
            return;
        }

        const handlers = getObjectValue(dynamicDataConfig, matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        for (const handlerObj of handlers) {
            const iDataType = (handlerObj as PlainJsonObject).iType as string | undefined;
            if (!iDataType) {
                continue;
            }

            if (this.shouldSkipHandler(iDataType, this._skipDynamicData)) {
                continue;
            }

            const paramFile = ((handlerObj as PlainJsonObject).iParamFile as string) || '';
            const resultKey = `${componentName}_${iDataType}`;

            if (!RegistryStore.containsData(iDataType)) {
                continue;
            }

            const dataHandler = RegistryStore.getDynamicData(iDataType);
            if (!dataHandler) {
                continue;
            }

            const params = this.getDynamicDataParams(dynamicConfigAppSite, paramFile);
            const paramJson: PlainJsonObject = {};
            if (params) {
                const entries: Iterable<[string, unknown]> = params instanceof Map ? params.entries() : Object.entries(params as PlainJsonObject);
                for (const [key, val] of entries) {
                    paramJson[key] = val instanceof JsonValue ? (val.asString() ?? val.asNumber() ?? val.asBool() ?? val) : val;
                }
            }

            try {
                // Data handlers resolve parent values the SAME way action handlers do: a non-empty inherited
                // parent value wins, otherwise fall back to the server-verified request context, surfacing
                // auth keys (UserId/IsAuthenticated/UserRole/UserAuthMode) to context-aware data providers.
                const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);

                const handlerWrapperJson = createJsonObjectWrapper(componentJson as PlainJsonObject | PlainJsonMap, true);
                const handlerParamJson = createJsonObjectWrapper(paramJson as PlainJsonObject | PlainJsonMap, true) as unknown as JsonObject;
                const handlerStart = performance.now();
                const resultJson = this.executeWithReadOnly(handlerParamJson, handlerWrapperJson as unknown as JsonObject, configJson, () =>
                    RegistryStore.invokeDynamicData(dataHandler.typeName, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, handlerParamJson, handlerWrapperJson as unknown as JsonObject, this.getTransientStore(), configJson, getParentJsonValue) ?? new JsonObject());
                this._dynamicDataNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
                this._dynamicDataCount++;

                if (resultJson !== null) {
                    if (this._config!.replaceHandlerReturn) {
                        if (componentJson instanceof Map) {
                            componentJson.clear();
                        } else {
                            for (const key of Object.keys(componentJson as PlainJsonObject)) {
                                delete (componentJson as PlainJsonObject)[key];
                            }
                        }
                    }
                    for (const key of getObjectKeys(resultJson)) {
                        let value: unknown = getObjectValue(resultJson, key);
                        if (value && (value as PlainJsonObject).constructor && (value as PlainJsonObject).constructor.name === 'JsonValue') {
                            const jv = value as JsonValue;
                            if (jv.isString) value = jv.asString();
                            else if (jv.isNumber) value = jv.asNumber();
                            else if (jv.isInteger) value = jv.asInteger();
                            else if (jv.isBool) value = jv.asBool();
                            else if (jv.isNull) value = null;
                            else if (jv.isArray) value = jv.asArray();
                            else if (jv.isObject) value = jv.asObject();
                        }
                        setObjectValue(componentJson, key, value);
                    }
                }

                // componentJson is read-only during the IData handler call. The loader
                // merges resultJson into componentJson between iterations, so multiple
                // entries for one component alias the latest merged state.
                this._dynamicResultsJson.dynamicDataResults.set(resultKey, componentJson as JsonObject);

                this.syncDynamicActionResults(componentName, componentJson as JsonObject);
            } catch (ex) {
                Logger.error(`ApplyDynamicDataHandlers: Error calling ${iDataType}: ${(ex as Error).message}`, 'LoaderPreProcessJson');
                // F-ENG3 (handler exception): an IData handler threw, so we log it and surface the exception — the
                // component keeps its un-enriched JSON and the failure is invisible to the response.
                Logger.fallback("F-ENG3",
                    `IData handler threw, component left un-enriched: handler=${iDataType} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                throw ex;
            }
        }
    },

    applyDynamicActionHandlers(
        this: LoaderPreProcessJson,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentJson: JsonObject | PlainJsonObject,
        configJson: JsonObject | null = null,
        flowStack: string[] | null = null
    ): void {
        if (!this._actionMode) {
            return;
        }
        const { config: dynamicConfig, configAppSite: dynamicConfigAppSite } = this.resolveDynamicActionConfigSource(appSite);
        if (!dynamicConfig) return;
        const matchedKey = this.getDynamicActionMatchKey(dynamicConfig, componentName);
        if (!matchedKey) return;
        const handlers = getObjectValue(dynamicConfig, matchedKey);
        if (!handlers || !Array.isArray(handlers)) return;

        const actionJson = configJson;
        for (const handlerObj of handlers as Array<PlainJsonObject>) {
            const iActionType = handlerObj.iType as string | undefined;
            if (!iActionType) continue;
            const paramFile = (handlerObj.iParamFile as string) || '';
            if (!RegistryStore.containsAction(iActionType)) continue;
            const actionHandler = RegistryStore.getDynamicAction(iActionType);
            if (!actionHandler) continue;

            const params = this.getDynamicActionParams(dynamicConfigAppSite, paramFile);
            const paramJson: PlainJsonObject = {};
            if (params) {
                const entries: Iterable<[string, unknown]> = params instanceof Map ? (params as PlainJsonMap).entries() : Object.entries(params as PlainJsonObject);
                for (const [key, val] of entries) {
                    paramJson[key] = val instanceof JsonValue ? (val.asString() ?? val.asNumber() ?? val.asBool() ?? val) : val;
                }
            }

            const actionResultKey = `${componentName}_${iActionType}`;

            try {
                // Parent-value lookup (parent-context snapshot + request context fallback), shared with data handlers.
                const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);

                const handlerStart = performance.now();
                this.executeDynamicAction(appSite, appView, appFile, componentName, resolvedAppSite, resolvedComponentName, paramJson, componentJson, actionJson, actionHandler, getParentJsonValue);
                this._dynamicActionNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
                this._dynamicActionCount++;

                // componentJson is read-only during DoAction (executeWithReadOnly) and
                // current IAction handlers communicate via transientStore, never mutating
                // componentJson. syncDynamicActionResults overwrites this entry after
                // any later data merge, so storing the reference here is safe.
                this._dynamicResultsJson.dynamicActionResults.set(actionResultKey, componentJson as JsonObject);
            } catch (ex) {
                Logger.error(`ApplyDynamicActionHandlers: Error calling ${iActionType}: ${(ex as Error).message}`, 'LoaderPreProcessJson');
            }
        }
    },

    /**
     * Resolves the params for a PURE client handler — one the AppSite declares with DataSource ⊇ "Pure" in
     * dynamicclientdata.json, meaning it produces its output from its declared params alone and never
     * consumes a server-produced raw island.
     *
     * A pure binding names the IClient processor in its IType (e.g. TimeClient), whereas an Embedded binding
     * names the IClientData preparer. That is what lets one config file carry both shapes without ambiguity,
     * and it is why this matches on the handler name off the element rather than on the island key.
     *
     * Returns null when the AppSite declares no pure binding for this component/handler, which is the signal
     * to skip the handler exactly as an absent island always has.
     */
    tryResolvePureClientHandlerParams(this: LoaderPreProcessJson, appSite: string, componentName: string, clientHandlerName: string): JsonObject | null {
        let dynamicConfig: DynamicConfigMap | null = this.getDynamicClientDataConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicClientDataConfig(trimmed);
                    if (dynamicConfig) {
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }
        if (!dynamicConfig) {
            return null;
        }

        let matchedKey: string | null = dynamicConfig.findKeyCaseInsensitive(componentName);
        if (!matchedKey && dynamicConfig.has('*')) {
            matchedKey = '*';
        }
        if (!matchedKey) {
            return null;
        }

        for (const handler of dynamicConfig.get(matchedKey) ?? []) {
            if (!handler.hasDataSource('Pure')) continue;
            if (handler.iType.toLowerCase() !== clientHandlerName.toLowerCase()) continue;

            return this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();
        }

        return null;
    },

    /**
     * Applies CLIENT-DATA preparer handlers from dynamicclientdata.json for a component. Runs the registered
     * IClientData producer (context-light, via RegistryStore.invokeDynamicClientData) and records the RAW set
     * into dynamicClientDataResults — it never mutates componentJson. The merge records ONLY when Embedded is
     * declared (page-1 raw ships with the render); a Remote-only binding is served on demand by
     * getClientDataSlice (the /api/handler-data endpoint, P3A), never produced at render. configJson is passed
     * as the producer's callerParams.
     */
    applyDynamicClientDataHandlers(this: LoaderPreProcessJson, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject, configJson: JsonObject | null, flowStack: string[] | null = null): void {
        let dynamicConfig: DynamicConfigMap | null = this.getDynamicClientDataConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicClientDataConfig(trimmed);
                    if (dynamicConfig) {
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }
        if (!dynamicConfig) {
            return;
        }

        let matchedKey: string | null = dynamicConfig.findKeyCaseInsensitive(componentName);
        if (!matchedKey && dynamicConfig.has('*')) {
            matchedKey = '*';
        }
        if (!matchedKey) {
            return;
        }

        const handlers = dynamicConfig.get(matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);

        for (const handler of handlers) {
            const iClientDataType: string = handler.iType;
            if (!iClientDataType) {
                continue;
            }

            // The merge produces the raw only for an Embedded (or Embedded,Remote hybrid) binding. A Remote-only
            // binding is served on demand by getClientDataSlice (the /api/handler-data endpoint), so skip it here.
            if (!handler.hasDataSource('Embedded')) {
                continue;
            }

            if (!RegistryStore.containsClientData(iClientDataType)) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicClientDataHandlers: IClientData handler '${iClientDataType}' not registered`, 'LoaderPreProcessJson');
                }
                continue;
            }

            const paramJson = this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();

            try {
                // F2: pin paramJson + componentJson + configJson read-only across the producer — ClientData
                // records the RAW set only and must never mutate the resolved component JSON (invariant I4).
                const handlerStart = performance.now();
                const rawJson = this.executeWithReadOnly(paramJson, componentJson, configJson,
                    () => RegistryStore.invokeDynamicClientData(iClientDataType, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, paramJson, componentJson, this.getTransientStore(), configJson, getParentJsonValue) ?? new JsonObject());
                this._dynamicClientDataNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
                this._dynamicClientDataCount++;
                this._dynamicResultsJson.dynamicClientDataResults.set(`${componentName}_${iClientDataType}`, JsonValue.object(rawJson));
            } catch (ex) {
                Logger.error(`ApplyDynamicClientDataHandlers: Error calling ${iClientDataType}: ${(ex as Error).message}`, 'LoaderPreProcessJson');
                // F-ENG3 (handler exception): an IClientData producer threw, so we log it and surface the exception.
                Logger.fallback('F-ENG3', `IClientData handler threw, island not produced: handler=${iClientDataType} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                throw ex;
            }
        }
    },

    /**
     * Reconstructs the component's raw template HTML from its HtmlJson structure by concatenating the static
     * ('s') / slot ('x') string chunks in document order. The data-client-handler declarations are literal
     * template markup (no placeholders), so each lives whole inside one static chunk — the joined string is the
     * source CommonUtil.extractClientHandlerDeclarations scans (the Json engines carry no OriginalContent).
     */
    reconstructComponentHtmlFromJson(htmlJson: JsonObject | null): string {
        if (!htmlJson || htmlJson.size === 0) {
            return '';
        }
        let result = '';
        for (const key of htmlJson.keys()) {
            if (key.length > 0 && (key[0] === 's' || key[0] === 'x')) {
                const val = htmlJson.getValue(key);
                if (val && val.isString) {
                    result += val.asString() ?? '';
                }
            }
        }
        return result;
    },

    /**
     * CLIENT-tier data consumption (§2B.5) — PreProcessJson (structured HtmlJson) mirror of the Normal string
     * engine's applyClientHandlers. For each client-handler declaration on the component template (parsed by
     * CommonUtil.extractClientHandlerDeclarations over the raw HTML reconstructed from the HtmlJson s/x chunks),
     * reads the DELIVERED island raw (_config.dynamicClientData) else the HTML island keyed
     * <ComponentName>_<ClientDataKey>, runs the registered IClient over it (F2 read-only pin), MERGES the
     * filtered output into resolvedJson, re-records the raw into dynamicClientDataResults and the FILTERED into
     * dynamicClientResults. Runs only on the client tier.
     */
    applyClientHandlers(this: LoaderPreProcessJson, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, htmlJson: JsonObject | null, resolvedJson: JsonObject, configJson: JsonObject | null, flowStack: string[] | null = null): void {
        const componentHtml = this.reconstructComponentHtmlFromJson(htmlJson);
        if (!componentHtml) {
            return;
        }

        const declarations = CommonUtil.extractClientHandlerDeclarations(componentHtml);
        if (declarations.length === 0) {
            return;
        }

        // Island raw source: the ConfigClient console round-trip carries the delivered map
        // (_config.dynamicClientData); a real browser has no such map and reads the island from the rendered DOM.
        const delivered = this._config!.dynamicClientData;

        const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);

        for (const decl of declarations) {
            if (!RegistryStore.containsClientHandler(decl.handlerName)) {
                continue;
            }

            let rawJson: JsonObject;
            let pureParamJson: JsonObject | null = null;
            // The island this declaration CONSUMES. Empty on the pure lane, which consumes none.
            let islandKey = '';
            // The key the consumed OUTPUT is recorded under. Always set, so a pure declaration is still
            // cross-checked by the round-trip: named for the preparer on the island lane (symmetric with
            // dynamicClientDataResults) and for the handler itself on the pure lane, which has no preparer.
            let resultKey = componentName + '_' + decl.handlerName;
            if (!decl.clientDataKey) {
                // PURE lane, DECLARED by the absence of data-client-data: this handler derives its output from
                // its params alone, so no island is looked up at all. The AppSite's dynamicclientdata binding
                // must still declare DataSource "Pure" for it to run, which keeps the JSON declaration the
                // security bound — a component cannot opt itself into island-free execution by editing its HTML.
                pureParamJson = this.tryResolvePureClientHandlerParams(appSite, componentName, decl.handlerName);
                if (pureParamJson === null) {
                    continue;
                }
                rawJson = new JsonObject();
            } else {
                islandKey = componentName + '_' + decl.clientDataKey;
                resultKey = islandKey;
                if (delivered !== null && delivered.has(islandKey) && delivered.get(islandKey).isObject) {
                    rawJson = delivered.get(islandKey).asObject()!;
                } else {
                    const rawStr = CommonUtil.extractClientDataIsland(componentHtml, islandKey);
                    // A DATA-DEPENDENT declaration named a preparer, so a missing island is a MISSING PRODUCER
                    // and the handler is skipped. ⛔ It is deliberately NOT retried on the pure path: the two
                    // lanes are disjoint by declaration now, so a broken producer can no longer be masked by a
                    // pure binding that happens to share the handler name.
                    if (!rawStr) {
                        continue;
                    }
                    rawJson = JsonUtility.parseJsonString(rawStr);
                    if (rawJson.size === 0) {
                        continue;
                    }
                }
            }

            try {
                // A pure handler reads its params from the BINDING (where FixedTime/DateFormat live alongside
                // the server tier's own copy), overlaid with the element's data-client-handler-param so a
                // per-instance override still wins. A data-dependent handler keeps reading the element only.
                let paramJson: JsonObject;
                if (pureParamJson === null) {
                    paramJson = JsonUtility.parseJsonString(decl.param) ?? new JsonObject();
                } else {
                    paramJson = pureParamJson;
                    const declOverride = JsonUtility.parseJsonString(decl.param);
                    if (declOverride !== null) {
                        for (const [k, v] of declOverride.entries()) {
                            paramJson.set(k, v);
                        }
                    }
                }
                // F2: pin the delivered island raw + paramJson read-only across the IClient invocation.
                const handlerStart = performance.now();
                const filtered = this.executeWithReadOnly(paramJson, rawJson, null,
                    () => RegistryStore.invokeDynamicClient(decl.handlerName, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, paramJson, resolvedJson, this.getTransientStore(), configJson, getParentJsonValue, rawJson) ?? new JsonObject());
                this._dynamicClientDataNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
                this._dynamicClientDataCount++;
                for (const [k, v] of filtered.entries()) {
                    resolvedJson.set(k, v);
                }
                // Re-emit the delivered island at the page tail, matching the server byte-for-byte. A PURE
                // handler consumed no island — rawJson is the empty stand-in built above — so there is nothing
                // to re-emit; emitting it would append an island the server never produced.
                if (pureParamJson === null) {
                    this._dynamicResultsJson.dynamicClientDataResults.set(islandKey, JsonValue.object(rawJson));
                }
                // Record the CONSUMED (filtered) output — the client mirror of the server DynamicData.
                // Keyed by resultKey, NOT islandKey: a pure declaration has no island key, and recording it
                // under an empty key would collide every pure handler in the component onto one slot.
                this._dynamicResultsJson.dynamicClientResults.set(resultKey, JsonValue.object(filtered));
            } catch (ex) {
                Logger.error(`ApplyClientHandlers: Error running ${decl.handlerName}: ${(ex as Error).message}`, 'LoaderPreProcessJson');
                // F-ENG3 (handler exception): an IClient consumer threw, so we log it and surface the exception.
                Logger.fallback('F-ENG3', `IClient handler threw, component left un-enriched: handler=${decl.handlerName} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                throw ex;
            }
        }
    },

    /**
     * P3A ON-DEMAND slice for the /api/handler-data endpoint. Resolves the client-data binding for handlerName
     * and returns the raw producer output via the SAME context-light producer the merge uses
     * (RegistryStore.invokeDynamicClientData) — one producer, two seams. Does NOT run a page merge; callerParams
     * is handed straight in as paging keys. Returns null when the AppSite declares no binding for the handler
     * (endpoint → 400, no arbitrary handler probing).
     *
     * Not gated on DataSource ⊇ Remote: that flag says WHERE the island is delivered at render time (Embedded =
     * with the server's own render), not who is allowed to ask for it. A client rendering the AppSite itself
     * needs exactly the data the Embedded island would have carried, so gating here would force every Embedded
     * AppSite to redeclare itself merely to be client-mergeable. The declaration in dynamicclientdata.json
     * remains the bound: an undeclared handler is still unreachable.
     */
    getClientDataSlice(this: LoaderPreProcessJson, appSite: string, handlerName: string, callerParams: JsonObject): JsonObject | null {
        if (!handlerName) {
            return null;
        }

        let dynamicConfig: DynamicConfigMap | null = this.getDynamicClientDataConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicClientDataConfig(trimmed);
                    if (dynamicConfig) {
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }
        if (!dynamicConfig) {
            return null;
        }

        for (const boundComponentName of dynamicConfig.keys()) {
            const handlers = dynamicConfig.get(boundComponentName);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }
            for (const handler of handlers) {
                if (!handler.iType || handler.iType.toLowerCase() !== handlerName.toLowerCase()) {
                    continue;
                }
                if (!RegistryStore.containsClientData(handlerName)) {
                    return null;
                }

                const paramJson = this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();
                // Remote/on-demand endpoint: no merge context, so the merge-only params are passed empty and the
                // per-request config rides in as configJson (the producer reads only appSite/component/paramJson).
                return RegistryStore.invokeDynamicClientData(handlerName, appSite, '', '', boundComponentName, 0, appSite, boundComponentName, paramJson, new JsonObject(), this.getTransientStore(), callerParams, null) ?? new JsonObject();
            }
        }

        return null;
    },

    /**
     * Produces every declared client-data island for the AppSite, keyed "<component>_<IType>" — the island set a
     * client merge of this AppSite needs when the page carries none of its own (see ILoader.getClientDataIslands).
     * Runs the same context-light producer as the merge and the single-slice endpoint, over every binding
     * dynamicclientdata.json declares, so the set is byte-identical to the islands a server render of this
     * AppSite would have embedded.
     */
    getClientDataIslands(this: LoaderPreProcessJson, appSite: string, callerParams: JsonObject): JsonObject {
        const islands = new JsonObject();

        let dynamicConfig: DynamicConfigMap | null = this.getDynamicClientDataConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicClientDataConfig(trimmed);
                    if (dynamicConfig) {
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }
        if (!dynamicConfig) {
            return islands;
        }

        for (const boundComponentName of dynamicConfig.keys()) {
            const handlers = dynamicConfig.get(boundComponentName);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }
            for (const handler of handlers) {
                if (!handler.iType || !RegistryStore.containsClientData(handler.iType)) {
                    continue;
                }

                const paramJson = this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();
                // Same shape as the merge's applyDynamicClientDataHandlers record: key by the BOUND component,
                // because that is the component the consumer's island lookup derives (component + "_" + key).
                const raw = RegistryStore.invokeDynamicClientData(handler.iType, appSite, '', '', boundComponentName, 0, appSite, boundComponentName, paramJson, new JsonObject(), this.getTransientStore(), callerParams, null);
                if (raw) {
                    islands.set(boundComponentName + '_' + handler.iType, JsonValue.object(raw));
                }
            }
        }

        return islands;
    },

    /**
     * True when the AppSite declares a client-data binding with a registered producer (see ILoader.hasClientData).
     * Same predicate as getClientDataIslands, declaration lookup only.
     */
    hasClientData(this: LoaderPreProcessJson, appSite: string): boolean {
        let dynamicConfig: DynamicConfigMap | null = this.getDynamicClientDataConfig(appSite);
        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicClientDataConfig(trimmed);
                    if (dynamicConfig) {
                        break;
                    }
                }
            }
        }
        if (!dynamicConfig) {
            return false;
        }

        for (const boundComponentName of dynamicConfig.keys()) {
            const handlers = dynamicConfig.get(boundComponentName);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }
            for (const handler of handlers) {
                if (handler.iType && RegistryStore.containsClientData(handler.iType)) {
                    return true;
                }
            }
        }

        return false;
    },

    applyDynamicViewHandlersJson(
        this: LoaderPreProcessJson,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        componentHtmlJson: JsonObject | null,
        componentJson: JsonObject | PlainJsonObject | null,
        resolvedAppSite: string,
        resolvedComponentName: string,
        configJson: JsonObject | null = null
    ): JsonObject | null {
        if (!componentHtmlJson || componentHtmlJson.size === 0) return componentHtmlJson;
        if (componentName.startsWith('!')) return componentHtmlJson;
        if (resolvedComponentName === this._config!.defaultSkipTemplateName || resolvedComponentName === this._config!.defaultReplaceTemplateName) return componentHtmlJson;
        return this._applyDynamicViewHandlersJson(appSite, appView, appFile, componentName, componentDepth, componentHtmlJson, componentJson, resolvedAppSite, resolvedComponentName, configJson);
    },

    _applyDynamicViewHandlersJson(
        this: LoaderPreProcessJson,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        componentHtmlJson: JsonObject,
        componentJson: JsonObject | PlainJsonObject | null,
        resolvedAppSite: string,
        resolvedComponentName: string,
        configJson: JsonObject | null = null
    ): JsonObject {
        let dynamicConfig = this.getDynamicViewConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicViewConfig(trimmed);
                    if (dynamicConfig) {
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }
        if (!dynamicConfig) {
            return componentHtmlJson;
        }

        const keysToProcess: string[] = [];
        if (hasKey(dynamicConfig, '*')) keysToProcess.push('*');

        let foundSpecificMatch = false;
        const specificMatchedKey = findKeyCaseInsensitive(dynamicConfig, componentName);
        if (specificMatchedKey && specificMatchedKey !== '*') {
            keysToProcess.push(specificMatchedKey);
            foundSpecificMatch = true;
        }

        if (!foundSpecificMatch) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const searchAppSite of searchAppSites.split(',')) {
                    const trimmed = searchAppSite.trim();
                    if (!trimmed) continue;
                    const searchConfig = this.getDynamicViewConfig(trimmed);
                    if (!searchConfig) continue;

                    const searchMatchedKey = findKeyCaseInsensitive(searchConfig, componentName);
                    if (searchMatchedKey && searchMatchedKey !== '*') {
                        keysToProcess.push(searchMatchedKey);
                        dynamicConfig = searchConfig;
                        dynamicConfigAppSite = trimmed;
                        foundSpecificMatch = true;
                        break;
                    }

                    if (!keysToProcess.includes('*') && hasKey(searchConfig, '*')) {
                        keysToProcess.unshift('*');
                        dynamicConfig = searchConfig;
                        dynamicConfigAppSite = trimmed;
                        break;
                    }
                }
            }
        }

        if (keysToProcess.length === 0) {
            return componentHtmlJson;
        }

        let resultHtmlJson = componentHtmlJson;
        for (const mk of keysToProcess) {
            const handlers = getObjectValue(dynamicConfig, mk);
            if (!handlers || !Array.isArray(handlers)) continue;

            for (const handlerObj of handlers as Array<PlainJsonObject>) {
                const iViewType = handlerObj.iType as string | undefined;
                if (!iViewType || this.shouldSkipHandler(iViewType, this._skipDynamicView)) continue;
                const paramFile = (handlerObj.iParamFile as string) || '';
                if (!RegistryStore.containsView(iViewType)) continue;
                const viewHandler = RegistryStore.getDynamicView(iViewType);
                if (!viewHandler) continue;

                const params = this.getDynamicViewParams(dynamicConfigAppSite, paramFile);
                const paramJson = new JsonObject();
                if (params) {
                    for (const key of getObjectKeys(params)) {
                        paramJson.set(key, getObjectValue(params, key));
                    }
                }

                let compJson: JsonObject;
                if (componentJson instanceof JsonObject) {
                    compJson = componentJson;
                } else if (componentJson && typeof componentJson === 'object' && !(componentJson instanceof JsonArray)) {
                    compJson = new JsonObject();
                    for (const key of getObjectKeys(componentJson)) {
                        compJson.set(key, getObjectValue(componentJson, key));
                    }
                } else {
                    compJson = new JsonObject();
                }

                // Dispatch through the registry, which owns the IView/IViewJson decision and the
                // null-argument guards. A null return means the registered handler is HTML-only,
                // so this JSON engine has nothing to apply and the component keeps its HtmlJson.
                const handlerStart = performance.now();
                const viewJsonResult = this.executeWithReadOnly(paramJson, compJson, configJson,
                    () => RegistryStore.invokeDynamicViewJson(viewHandler.typeName, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, resultHtmlJson, paramJson, compJson, configJson));
                if (viewJsonResult !== null) {
                    this._dynamicViewNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
                    this._dynamicViewCount++;
                    resultHtmlJson = viewJsonResult;
                }

                const viewJsonResultKey = `${componentName}_${iViewType}`;
                const resultHtml = normalizeFileContent(GenerateHtmlJson.convertJsonToHtml(resultHtmlJson));
                this._dynamicResultsJson.dynamicViewResults.set(viewJsonResultKey, resultHtml);
            }
        }

        return resultHtmlJson;
    },

    executeDynamicAction(this: LoaderPreProcessJson, appSite: string, appView: string, appFile: string, componentName: string, resolvedAppSite: string, resolvedComponentName: string, paramJson: PlainJsonObject, componentJson: JsonObject | PlainJsonObject, actionJson: JsonObject | null, actionHandler: NonNullable<ReturnType<typeof RegistryStore.getDynamicAction>>, getParentJsonValue: ((key: string) => string | null) | null): void {
        const wrapperJson = createJsonObjectWrapper(componentJson as PlainJsonObject | PlainJsonMap, true);
        const wrapperParamJson = createJsonObjectWrapper(paramJson as unknown as PlainJsonObject | PlainJsonMap, true);
        const wrapperActionJson = actionJson;
        this.executeWithReadOnly(wrapperParamJson as unknown as JsonObject, wrapperJson as unknown as JsonObject, wrapperActionJson as unknown as JsonObject | null, () => {
            RegistryStore.invokeDynamicAction(actionHandler.typeName, appSite, appView, appFile, componentName, resolvedAppSite, resolvedComponentName, wrapperParamJson as unknown as JsonObject, wrapperJson as unknown as JsonObject, this.getTransientStore(), wrapperActionJson as unknown as JsonObject, getParentJsonValue);
            return null;
        });
    },

    executeWithReadOnly<T>(this: LoaderPreProcessJson, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, action: () => T): T {
        const paramWasReadOnly = paramJson.isReadOnly;
        const componentWasReadOnly = componentJson.isReadOnly;
        const configWasReadOnly = configJson ? configJson.isReadOnly : false;

        paramJson.isReadOnly = true;
        componentJson.isReadOnly = true;
        if (configJson) {
            configJson.isReadOnly = true;
        }

        try {
            return action();
        } finally {
            paramJson.isReadOnly = paramWasReadOnly;
            componentJson.isReadOnly = componentWasReadOnly;
            if (configJson) {
                configJson.isReadOnly = configWasReadOnly;
            }
        }
    },

    syncDynamicActionResults(this: LoaderPreProcessJson, componentName: string, afterJson: unknown): void {
        if (!this._actionMode || afterJson === null || afterJson === undefined) {
            return;
        }

        const prefix = `${componentName}_`.toLowerCase();
        for (const key of Array.from(this._dynamicResultsJson.dynamicActionResults.keys())) {
            if (key.toLowerCase().startsWith(prefix)) {
                this._dynamicResultsJson.dynamicActionResults.set(key, afterJson as never);
            }
        }
    },

    resolveDynamicDataConfigSource(this: LoaderPreProcessJson, appSite: string): { config: DynamicConfigMap | null; configAppSite: string } {
        let config = this.getDynamicDataConfig(appSite);
        if (config) {
            return { config, configAppSite: appSite };
        }

        const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites) {
            for (const searchAppSite of searchAppSites.split(',')) {
                const trimmed = searchAppSite.trim();
                if (!trimmed) continue;
                config = this.getDynamicDataConfig(trimmed);
                if (config) {
                    return { config, configAppSite: trimmed };
                }
            }
        }

        return { config: null, configAppSite: appSite };
    },

    getDynamicDataMatchKey(this: LoaderPreProcessJson, dynamicConfig: DynamicConfigMap, componentName: string): string | null {
        const matchedKey = findKeyCaseInsensitive(dynamicConfig, componentName);
        if (matchedKey) {
            return matchedKey;
        }
        if (hasKey(dynamicConfig, '*')) {
            return '*';
        }
        return null;
    },

    resolveDynamicActionConfigSource(this: LoaderPreProcessJson, appSite: string): { config: DynamicConfigMap | null; configAppSite: string } {
        let config = this.getDynamicActionConfig(appSite);
        if (config) {
            return { config, configAppSite: appSite };
        }

        const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites) {
            for (const searchAppSite of searchAppSites.split(',')) {
                const trimmed = searchAppSite.trim();
                if (!trimmed) continue;
                config = this.getDynamicActionConfig(trimmed);
                if (config) {
                    return { config, configAppSite: trimmed };
                }
            }
        }

        return { config: null, configAppSite: appSite };
    },

    getDynamicActionMatchKey(this: LoaderPreProcessJson, dynamicConfig: DynamicConfigMap, componentName: string): string | null {
        const matchedKey = findKeyCaseInsensitive(dynamicConfig, componentName);
        if (matchedKey) {
            return matchedKey;
        }
        if (hasKey(dynamicConfig, '*')) {
            return '*';
        }
        return null;
    },

    _applyDynamicDataHandlersSync(this: LoaderPreProcessJson, appSite: string, componentName: string, componentJson: PlainJsonObject): void {
        const dynamicConfig = this.getDynamicDataConfig(appSite);
        if (!dynamicConfig) {
            return;
        }

        const matchedKey = dynamicConfig.findKeyCaseInsensitive(componentName);
        if (!matchedKey) {
            return;
        }

        const handlers = dynamicConfig.get(matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        for (const handlerObj of handlers) {
            const iDataType = handlerObj.iType;
            if (!iDataType) {
                continue;
            }

            const paramFile = handlerObj.iParamFile || '';
            const resultKey = `${componentName}_${iDataType}`;
            if (this._dynamicResultsJson.dynamicDataResults && this._dynamicResultsJson.dynamicDataResults.has(resultKey)) {
                const cachedData = this._dynamicResultsJson.dynamicDataResults.get(resultKey).asObject();
                if (cachedData) {
                    for (const [key, value] of cachedData) {
                        componentJson[key] = value;
                    }
                }
                continue;
            }

            if (!RegistryStore.containsData(iDataType)) {
                continue;
            }

            const dataHandler = RegistryStore.getDynamicData(iDataType);
            if (!dataHandler) {
                continue;
            }

            const params = this.getDynamicDataParams(appSite, paramFile);
            const paramJsonObj = params ?? new JsonObject();
            const wrapperJsonObj = new JsonObject(componentJson || {});

            try {
                const resultJson = this.executeWithReadOnly(paramJsonObj, wrapperJsonObj, null, () =>
                    RegistryStore.invokeDynamicData(dataHandler.typeName, appSite, '', '', componentName, 0, appSite, componentName, paramJsonObj, wrapperJsonObj, this.getTransientStore(), null) ?? new JsonObject());
                if (resultJson) {
                    for (const [key, _value] of resultJson) {
                        componentJson[key] = resultJson.get(key);
                    }
                }
            } catch (ex) {
                Logger.error(`ApplyDynamicDataHandlers: Error calling ${iDataType}: ${(ex as Error).message}`, 'LoaderPreProcessJson');
                // F-ENG3 (handler exception): an IData handler threw, so we log it and surface the exception — the
                // component keeps its un-enriched JSON and the failure is invisible to the response.
                Logger.fallback("F-ENG3",
                    `IData handler threw, component left un-enriched: handler=${iDataType} component=${componentName} appSite=${appSite} appView= ex=${(ex as Error).message}`);
                throw ex;
            }

            for (const key of Object.keys(componentJson)) {
                const value = componentJson[key];
                if (value instanceof JsonArray) {
                    componentJson[key] = this._convertJsonArrayToPlain(value);
                } else if (value instanceof JsonObject) {
                    componentJson[key] = this._convertJsonObjectToPlain(value);
                }
            }
        }
    },

    _convertJsonArrayToPlain(this: LoaderPreProcessJson, jsonArray: JsonArray): unknown[] {
        const plainArray: unknown[] = [];
        for (let i = 0; i < jsonArray.length; i++) {
            let item: unknown = jsonArray[i];
            if (item instanceof JsonValue) {
                if (item.isObject) {
                    item = item.asObject();
                } else if (item.isArray) {
                    item = item.asArray();
                } else {
                    plainArray.push(this._unwrapJsonValue(item));
                    continue;
                }
            }
            if (item instanceof JsonObject) {
                plainArray.push(this._convertJsonObjectToPlain(item));
            } else if (item instanceof JsonArray) {
                plainArray.push(this._convertJsonArrayToPlain(item));
            } else {
                plainArray.push(item);
            }
        }
        return plainArray;
    },

    _convertJsonObjectToPlain(this: LoaderPreProcessJson, jsonObject: JsonObject): PlainJsonObject {
        const plainObject: PlainJsonObject = {};
        for (const key of jsonObject.keys()) {
            let value: unknown = jsonObject.get(key);
            if (value instanceof JsonValue) {
                if (value.isObject) {
                    value = value.asObject();
                } else if (value.isArray) {
                    value = value.asArray();
                } else {
                    plainObject[key] = this._unwrapJsonValue(value);
                    continue;
                }
            }
            if (value instanceof JsonObject) {
                plainObject[key] = this._convertJsonObjectToPlain(value);
            } else if (value instanceof JsonArray) {
                plainObject[key] = this._convertJsonArrayToPlain(value);
            } else {
                plainObject[key] = value;
            }
        }
        return plainObject;
    },

    _unwrapJsonValue(this: LoaderPreProcessJson, jsonValue: JsonValue): unknown {
        switch (jsonValue.typeName) {
            case JsonValueType.String:
                return jsonValue.asString();
            case JsonValueType.Number:
                return jsonValue.asNumber();
            case JsonValueType.Integer:
                return jsonValue.asInteger();
            case JsonValueType.Bool:
                return jsonValue.asBool();
            case JsonValueType.Array: {
                const arr = jsonValue.asArray();
                return arr ? this._convertJsonArrayToPlain(arr) : [];
            }
            case JsonValueType.Object: {
                const obj = jsonValue.asObject();
                return obj ? this._convertJsonObjectToPlain(obj) : {};
            }
            case JsonValueType.Null:
                return null;
            default:
                return null;
        }
    },

    /**
     * Resolves the AppSite/AppView to render by running the IAppSite handlers configured for the
     * requested AppSite (dynamicappsite.json, keyed by AppSite name). AppSite-specific handlers run
     * first, then the wildcard ("*") handlers LAST. Dormant infrastructure until IAppSite handlers are
     * registered and the merge pipeline calls it (later phase).
     */
    applyDynamicAppSiteHandlers(this: LoaderPreProcessJson, appSite: string, appView: string | null, contextJson: JsonObject, configJson: JsonObject | null): AppSiteResult {
        this._requestContextJson = contextJson;
        // Reset the per-merge navigation trace; one decision is appended per handler invocation below.
        this._lastNavigationTrace = [];

        let curAppSite = appSite;
        let curAppView = appView ?? '';

        const appSiteConfig = this.getDynamicAppSiteConfig(appSite);
        if (!appSiteConfig) return new AppSiteResult(curAppSite, curAppView);

        const specificHandlers = appSiteConfig.get(appSite);
        const wildcardHandlers = appSite === '*' ? null : appSiteConfig.get('*');
        const handlers: DynamicConfig[] = [];
        if (specificHandlers && Array.isArray(specificHandlers)) handlers.push(...specificHandlers);
        if (wildcardHandlers && Array.isArray(wildcardHandlers)) handlers.push(...wildcardHandlers);
        if (handlers.length === 0) return new AppSiteResult(curAppSite, curAppView);

        for (const handler of handlers) {
            const iAppSiteType = handler.iType;
            if (!iAppSiteType) {
                continue;
            }

            const provider = RegistryStore.getDynamicAppSite(iAppSiteType);
            if (!provider) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicAppSiteHandlers: IAppSite handler '${iAppSiteType}' not registered`, 'LoaderPreProcessJson');
                }
                continue;
            }

            const paramJson = this.getDynamicAppSiteParams(appSite, handler.iParamFile || '') ?? new JsonObject();
            try {
                const resolved = RegistryStore.invokeDynamicAppSite(provider.typeName, curAppSite, curAppView, paramJson, contextJson, configJson);
                if (resolved) {
                    const changed = curAppSite !== resolved.appSite || curAppView !== resolved.appView;
                    if (changed) {
                        Logger.flow(`ApplyDynamicAppSiteHandlers: redefined ${curAppSite}/${curAppView} -> ${resolved.appSite}/${resolved.appView} via ${iAppSiteType}`, 'PreProcessJson.MergeLifecycle');
                    }
                    // Preserve every hop (change or no-op) so a scenario can assert the path, not only the endpoint.
                    this._lastNavigationTrace.push(new NavigationDecision(
                        iAppSiteType, curAppSite, curAppView, resolved.appSite, resolved.appView, changed, resolved.reason));
                    curAppSite = resolved.appSite;
                    curAppView = resolved.appView;
                }
            } catch (ex) {
                // F-SEC5 (hardened, fail-closed): an AppSite handler computes the authoritative auth redirect
                // (e.g. unauth Admin -> Login). If it throws, keeping curAppSite/curAppView at the requested
                // (protected) values would render the PROTECTED page — a fail-OPEN. Surface the failure instead.
                Logger.error(`ApplyDynamicAppSiteHandlers: Error calling ${iAppSiteType}: ${(ex as Error).message}`, 'LoaderPreProcessJson');
                Logger.fallback("F-SEC5",
                    `AppSite handler ${iAppSiteType} threw; failing closed (would have kept protected appSite=${curAppSite}/${curAppView}): ${(ex as Error).message}`);
                throw ex;
            }
        }

        if (curAppSite !== appSite) {
            this.ensureAppSiteLoaded(curAppSite);
        }

        return new AppSiteResult(curAppSite, curAppView);
    },
};

